import type { ColumnGroupType, ColumnType, ColumnsType } from 'antd/es/table';

function isColumnGroup<T>(
  column: ColumnType<T> | ColumnGroupType<T>,
): column is ColumnGroupType<T> {
  return Array.isArray((column as ColumnGroupType<T>).children);
}

/** Last visible data column (ignores nested groups’ trailing leaves). */
export function getEvimoTableLastLeafColumn<T>(
  columns: ColumnsType<T>,
): ColumnType<T> | undefined {
  for (let index = columns.length - 1; index >= 0; index -= 1) {
    const column = columns[index];
    if (isColumnGroup(column)) {
      const nested = getEvimoTableLastLeafColumn(column.children);
      if (nested) {
        return nested;
      }
      continue;
    }
    return column;
  }
  return undefined;
}

export type PrepareEvimoTableColumnsResult<T> = {
  columns: ColumnsType<T> | undefined;
  stickyLastColumn: boolean;
};

export const EVIMO_TABLE_STICKY_ACTIONS_CELL_CLASS =
  'evimo-table-sticky-actions-cell';

type AntTableCellMeta = { className?: string };

function mergeAntTableCellClassName<
  T extends (...args: never[]) => AntTableCellMeta,
>(existing: T | undefined, extraClassName: string): T {
  const merged = ((...args: Parameters<T>) => {
    const prev = existing?.(...args) ?? {};
    const className = [prev.className, extraClassName]
      .filter(Boolean)
      .join(' ');
    return { ...prev, className };
  }) as T;
  return merged;
}

function withStickyActionsCellClass<T>(column: ColumnType<T>): ColumnType<T> {
  return {
    ...column,
    onHeaderCell: mergeAntTableCellClassName(
      column.onHeaderCell,
      EVIMO_TABLE_STICKY_ACTIONS_CELL_CLASS,
    ),
    onCell: mergeAntTableCellClassName(
      column.onCell,
      EVIMO_TABLE_STICKY_ACTIONS_CELL_CLASS,
    ),
  };
}

function mapEvimoTableLastLeafColumn<T>(
  columns: ColumnsType<T>,
  mapper: (column: ColumnType<T>) => ColumnType<T>,
): ColumnsType<T> {
  const next = [...columns];
  for (let index = next.length - 1; index >= 0; index -= 1) {
    const column = next[index];
    if (isColumnGroup(column)) {
      const children = mapEvimoTableLastLeafColumn(column.children, mapper);
      if (children !== column.children) {
        next[index] = { ...column, children };
        return next;
      }
      continue;
    }
    next[index] = mapper(column);
    return next;
  }
  return next;
}

/**
 * Ant Design `fixed: 'right'` duplicates the header row and misaligns cells with
 * `tableLayout="fixed"` + horizontal scroll. Evimo tables use CSS sticky on the
 * last column instead (`evimo-table--sticky-last-col`).
 */
export function prepareEvimoTableColumns<T>(
  columns: ColumnsType<T> | undefined,
  stickyLastColumn: boolean,
): PrepareEvimoTableColumnsResult<T> {
  if (!columns?.length) {
    return { columns, stickyLastColumn };
  }

  const lastLeaf = getEvimoTableLastLeafColumn(columns);
  if (!lastLeaf) {
    return { columns, stickyLastColumn };
  }

  const wantsSticky = stickyLastColumn || lastLeaf.fixed === 'right';
  if (!wantsSticky) {
    return { columns, stickyLastColumn: false };
  }

  const prepared = mapEvimoTableLastLeafColumn(columns, (column) => {
    const next = { ...column };
    if (next.fixed === 'right') {
      delete next.fixed;
    }
    return withStickyActionsCellClass(next);
  });

  return { columns: prepared, stickyLastColumn: true };
}
